import request from '@/utils/request';
import { normalizeOrderPrice, normalizeOrderQuantity } from '@/utils/order-payload-normalize';
import { ORDER_BOOK_DEPTH_LEVEL } from '@/utils/market-depth-map';

/** 下单链路（创建 / 签名 / 撮合）可能涉及链上操作，超时长于默认 10s */
const TRADE_ORDER_API_TIMEOUT_MS = 60_000;

/** 平台钱包余额查询（wallet/detail） */
const WALLET_DETAIL_TIMEOUT_MS = 30_000;

/**
 * liyuan-trade 用户侧接口封装。
 *
 * FairOdds：赔率走 /market/price、/market/batchPrice；
 * 用户订单簿走 /order/depth（HTTP 快照）与 /ws/depth（内存簿推送）；最新成交走 /order/ticker。
 * 禁止调用 /maker/*（做市内部）。
 *
 * 暂未对接（按需迭代）：
 * - /strategy/* — 策略中心
 * - /rc/deposits — 充值入账轮询
 * - /rc/balances、/rc/balance/{tokenId} — RC 链上托管余额（提现/充值链参数；平台余额以 /wallet/detail 为准）
 * - /position/update — 后端成交后自动更新，前端无需调用
 * - /maker/*、/inner/* — 内部/做市接口
 */

/**
 * 统计用户总持仓 GET /liyuan-trade/position/total
 * @param {Object} [params]
 * @param {string} [params.symbol] 交易对，为空则统计所有持仓
 */
export function getPositionTotal(params = {}) {
  const query = {};
  if (params.symbol != null && String(params.symbol).trim() !== '') {
    query.symbol = String(params.symbol).trim();
  }
  return request.get('/liyuan-trade/position/total', query);
}

/**
 * 查询用户钱包 GET /liyuan-trade/wallet/detail
 * @param {Object} params
 * @param {string} params.currency 币种，如 DUSD（平台交易本位，UI 展示 USD）、POINTS（积分）
 * @returns {Promise<{ available: number, frozen: number, total: number, currency: string, id?: string, userId?: string, createAt?: string, updateAt?: string }>}
 */
export function getWalletDetail(params = {}, options = {}) {
  const currency = String(params.currency ?? 'DUSD').trim();
  if (!currency) return Promise.reject(new Error('currency required'));
  return request.get(
    '/liyuan-trade/wallet/detail',
    { currency },
    {
      timeout: WALLET_DETAIL_TIMEOUT_MS,
      ...options,
    }
  );
}

/**
 * 三分钱包余额 GET /liyuan-trade/wallet/balances
 * @returns {Promise<{ points: string|number, dusd: string|number, activityDusd: string|number }>}
 */
export function getWalletBalances(options = {}) {
  return request.get(
    '/liyuan-trade/wallet/balances',
    {},
    {
      timeout: WALLET_DETAIL_TIMEOUT_MS,
      ...options,
    }
  );
}

/**
 * 分页查询当前用户余额记录 GET /liyuan-trade/balance-log/page
 * @param {Object} [params]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 * @param {string} [params.currency] 币种：USDC | DUSD | POINTS | TRIAL
 * @param {string} [params.direction] INCREASE | DECREASE
 * @param {string} [params.txnType] DEPOSIT | WITHDRAW | TRADE_CREDIT | REDEEM | ...
 */
export function getBalanceLogPage(params = {}) {
  return request.get('/liyuan-trade/balance-log/page', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
    ...(params.currency ? { currency: params.currency } : {}),
    ...(params.direction ? { direction: params.direction } : {}),
    ...(params.txnType ? { txnType: params.txnType } : {}),
  });
}

/**
 * 查询用户交易流水 GET /liyuan-trade/wallet/transactions
 * @param {Object} [params]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 * @param {string} [params.currency] 币种，为空则返回全部
 * @param {string} [params.txnType] ADMIN_LOCK | DEPOSIT | WITHDRAW | ...
 * @param {string} [params.direction] INCREASE | DECREASE
 * @param {string} [params.refType] ORDER | RECHARGE | WITHDRAW
 */
export function getWalletTransactions(params = {}) {
  return request.get('/liyuan-trade/wallet/transactions', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
    ...(params.currency ? { currency: params.currency } : {}),
    ...(params.txnType ? { txnType: params.txnType } : {}),
    ...(params.direction ? { direction: params.direction } : {}),
    ...(params.refType ? { refType: params.refType } : {}),
  });
}

/**
 * 查询用户持仓列表（可按 userId 查他人）GET /liyuan-trade/position/positionPageQuery
 * OpenAPI：查询所有用户持仓列表（做市商用）；公开资料页试用。
 *
 * @param {Object} [params]
 * @param {string|number} [params.userId]
 * @param {string} [params.collId]
 * @param {string} [params.symbol]
 * @param {string} [params.outcome]
 * @param {string} [params.question]
 * @param {string} [params.avgPriceMin]
 * @param {string} [params.avgPriceMax]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=50]
 * @returns {Promise<{ rows: Array, total: number }>}
 */
export function getPositionPageQuery(params = {}) {
  const query = {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 50,
  };
  if (params.userId != null && String(params.userId).trim() !== '') {
    query.userId = String(params.userId).trim();
  }
  const collId = String(params.collId ?? '').trim();
  if (collId) query.collId = collId;
  const symbol = String(params.symbol ?? '').trim();
  if (symbol) query.symbol = symbol;
  const outcome = String(params.outcome ?? '').trim();
  if (outcome) query.outcome = outcome;
  const question = String(params.question ?? '').trim();
  if (question) query.question = question;
  if (params.avgPriceMin != null && String(params.avgPriceMin).trim() !== '') {
    query.avgPriceMin = String(params.avgPriceMin).trim();
  }
  if (params.avgPriceMax != null && String(params.avgPriceMax).trim() !== '') {
    query.avgPriceMax = String(params.avgPriceMax).trim();
  }
  return request.get('/liyuan-trade/position/positionPageQuery', query);
}

/**
 * 分页查询当前用户持仓列表 GET /liyuan-trade/position/pageMyPosition
 *
 * 可按 collId / symbol / outcome 过滤；市场详情页用 collId 一次拉取当前 collection 全部持仓。
 *
 * @param {Object} [params]
 * @param {string} [params.collId] collection 路由 id
 * @param {string} [params.symbol] markets[].id
 * @param {string} [params.outcome]
 * @param {string} [params.question]
 */
export function getPageMyPosition(params = {}) {
  const query = {};
  const collId = String(params.collId ?? '').trim();
  if (collId) query.collId = collId;
  const symbol = String(params.symbol ?? '').trim();
  if (symbol) query.symbol = symbol;
  const outcome = String(params.outcome ?? '').trim();
  if (outcome) query.outcome = outcome;
  const question = String(params.question ?? '').trim();
  if (question) query.question = question;
  // 后端默认 pageSize=10；体育集合子盘多，详情页必须一次拉全量可卖持仓
  const pageNum = Number(params.pageNum);
  const pageSize = Number(params.pageSize);
  query.pageNum = Number.isFinite(pageNum) && pageNum > 0 ? pageNum : 1;
  query.pageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.min(pageSize, 200) : 200;
  return request.get('/liyuan-trade/position/pageMyPosition', query);
}

/**
 * 查询当前用户全平台持仓列表 GET /liyuan-trade/position/list
 *
 * 返回用户在**所有市场**上的持仓快照，无 symbol 过滤。
 * 适用：账户页 / portfolio 总览。
 * 不适用：市场详情页（详情页请用 getPageMyPosition({ collId })）。
 */
export function getPositionList() {
  return request.get('/liyuan-trade/position/list');
}

/** 修改持仓结算状态 PUT /liyuan-trade/position/settleStatus（提取奖金 settleStatus=2 须带 txHash） */
export function settleStatus(data) {
  // 列表 VO 的 id 为 string；后端绑 int64，须传 number，否则「参数错误」且仓位仍显示可提取
  const idNum = Number(data?.id);
  if (!Number.isFinite(idNum) || idNum <= 0) {
    return Promise.reject(new Error('invalid position id'));
  }
  const txHash = String(data?.txHash ?? data?.tx_hash ?? '').trim();
  const payload = {
    ...data,
    id: idNum,
    settleStatus: Number(data?.settleStatus),
  };
  if (txHash) payload.txHash = txHash;
  return request.put('/liyuan-trade/position/settleStatus', payload);
}

/** 账本-only 赢单恢复：CTF=0 且链上 dUSD≥中心化时补记 REDEEM POST /liyuan-trade/position/recoverRedeem */
export function recoverRedeemSettlement(data) {
  const idNum = Number(data?.id);
  if (!Number.isFinite(idNum) || idNum <= 0) {
    return Promise.reject(new Error('invalid position id'));
  }
  return request.post('/liyuan-trade/position/recoverRedeem', { id: idNum });
}

/**
 * 分页查询我的订单 GET /liyuan-trade/order/myOrders
 * @param {Object} [params]
 * @param {string} [params.symbol] 市场标识（市场详情 markets 数组项 id）
 * @param {string} [params.outcome] YES / NO 或 outcomes 原值
 * @param {string} [params.side] BUY | SELL
 * @param {string[]} [params.statusList] PENDING | NEW | PARTIALLY_FILLED | FILLED | CANCELLED | REJECTED
 * @param {number} [params.pageNum]
 * @param {number} [params.pageSize]
 * @returns {Promise<{ rows: Array, total: number }>}
 */
export function getMyOrders(params = {}) {
  const query = {};
  const symbol = String(params.symbol ?? '').trim();
  if (symbol) query.symbol = symbol;
  if (params.outcome) query.outcome = String(params.outcome).trim();
  if (params.side) query.side = String(params.side).trim().toUpperCase();
  if (Array.isArray(params.statusList) && params.statusList.length) {
    query.statusList = params.statusList
      .map((s) =>
        String(s ?? '')
          .trim()
          .toUpperCase()
      )
      .filter(Boolean);
  }
  if (params.pageNum != null) query.pageNum = params.pageNum;
  if (params.pageSize != null) query.pageSize = params.pageSize;
  return request.get('/liyuan-trade/order/myOrders', query, {
    paramsSerializer: { indexes: null },
  });
}

/**
 * 个人中心「订单」：仅挂单中 GET /liyuan-trade/order/openOrders
 * @param {{ pageNum?: number, pageSize?: number, symbol?: string }} [params]
 */
export function getOpenOrders(params = {}) {
  const query = {};
  const symbol = String(params.symbol ?? '').trim();
  if (symbol) query.symbol = symbol;
  if (params.pageNum != null) query.pageNum = params.pageNum;
  if (params.pageSize != null) query.pageSize = params.pageSize;
  return request.get('/liyuan-trade/order/openOrders', query);
}

/**
 * 个人中心「交易」历史活动流：成交买卖 / 已取消 / 拒绝 / 提取奖金
 * GET /liyuan-trade/order/activity/page
 * 进行中挂单不在此接口；FE `useAccountTradeActivity` 另拉 openOrders 并允许 cancelOrder。
 * @param {{ pageNum?: number, pageSize?: number }} [params]
 */
export function getTradeActivityPage(params = {}) {
  const query = {};
  if (params.pageNum != null) query.pageNum = params.pageNum;
  if (params.pageSize != null) query.pageSize = params.pageSize;
  return request.get('/liyuan-trade/order/activity/page', query);
}

/**
 * 解析撤单接口 data：true 成功，false 失败
 * @param {unknown} payload request 拦截器返回值（通常为 boolean，也可能是完整响应体）
 * @returns {boolean}
 */
export function parseCancelOrderResult(payload) {
  if (payload === true) return true;
  if (payload === false) return false;
  if (payload && typeof payload === 'object') {
    const inner = payload.data;
    if (inner === true) return true;
    if (inner === false) return false;
  }
  return false;
}

/**
 * 撤销订单 DELETE /liyuan-trade/order/order/{symbol}/{orderId}
 * @param {string} symbol 市场标识（markets 数组项 id）
 * @param {number|string} orderId 订单 ID
 * @returns {Promise<boolean>} 接口 data：true 撤单成功，false 撤单失败
 */
export function cancelOrder(symbol, orderId) {
  const sym = String(symbol ?? '').trim();
  const id = String(orderId ?? '').trim();
  if (!sym) return Promise.reject(new Error('symbol required'));
  if (!id) return Promise.reject(new Error('orderId required'));
  return request
    .delete(`/liyuan-trade/order/order/${encodeURIComponent(sym)}/${encodeURIComponent(id)}`)
    .then(parseCancelOrderResult);
}

/**
 * 查询订单簿深度 GET /liyuan-trade/order/depth/{symbol}
 * @param {string} symbol 交易对（collection/get 返回的 markets[].id）
 * @param {Object} [params]
 * @param {string} [params.outcome] collection/get 返回的 outcomes 原值（如 Yes / No）
 * @param {number} [params.level] 每侧档位数（默认 ORDER_BOOK_DEPTH_LEVEL；服务端可截断）
 */
export function getDepth(symbol, params = {}) {
  const sym = String(symbol ?? '').trim();
  if (!sym) return Promise.reject(new Error('symbol required'));
  const query = { level: params.level ?? ORDER_BOOK_DEPTH_LEVEL };
  if (params.outcome) query.outcome = params.outcome;
  return request.get(`/liyuan-trade/order/depth/${encodeURIComponent(sym)}`, query);
}

/**
 * 批量查询展示订单簿 POST /liyuan-trade/order/depth/batch
 * @param {Array<{symbol:string,outcome:string,level?:number}>} items
 */
export function getDepthBatch(items) {
  const list = Array.isArray(items) ? items : [];
  return request.post('/liyuan-trade/order/depth/batch', { items: list });
}

/**
 * 推荐订单角色 GET /liyuan-trade/order/depth/role/recommend/{symbol}
 * 调用方应传对手方向 side（用户 BUY → 传 SELL，反之亦然）。
 * @param {string} symbol 市场标识（markets[].id）
 * @param {Object} params
 * @param {string} params.outcome collection/get 返回的 outcomes 原值
 * @param {number} params.price 委托价格（美元）
 * @param {number} params.quantity 委托数量
 * @param {'BUY'|'SELL'} params.side 对手方向 BUY/SELL
 * @returns {Promise<'MAKER'|'TAKER'|string>}
 */
export function recommendTradeRole(symbol, params = {}, options = {}) {
  const sym = String(symbol ?? '').trim();
  const outcome = String(params.outcome ?? '').trim();
  const side = String(params.side ?? '')
    .trim()
    .toUpperCase();
  if (!sym) return Promise.reject(new Error('symbol required'));
  if (!outcome) return Promise.reject(new Error('outcome required'));
  if (side !== 'BUY' && side !== 'SELL') {
    return Promise.reject(new Error('side required'));
  }
  const price = normalizeOrderPrice(params.price);
  const quantity = normalizeOrderQuantity(params.quantity);
  if (!Number.isFinite(price) || price <= 0) {
    return Promise.reject(new Error('invalid price'));
  }
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return Promise.reject(new Error('invalid quantity'));
  }

  return request.get(
    `/liyuan-trade/order/depth/role/recommend/${encodeURIComponent(sym)}`,
    { outcome, price, quantity, side },
    {
      timeout: TRADE_ORDER_API_TIMEOUT_MS,
      ...options,
    }
  );
}

/**
 * FairOdds 实时单价 GET /liyuan-trade/market/price
 * 不传 outcomes：后端按库内 Outcomes 自适应（短周期 Up/Down，长周期 Yes/No）。
 * @param {string} symbol 市场标识（markets[].id）
 * @returns {Promise<Record<string, string>|string|unknown>} Map[outcome→price]（兼容旧数组）
 */
export function getMarketPrice(symbol) {
  const sym = String(symbol ?? '').trim();
  if (!sym) return Promise.reject(new Error('symbol required'));
  return request.get(`/liyuan-trade/market/price?symbol=${encodeURIComponent(sym)}`);
}

/**
 * FairOdds 批量询价 POST /liyuan-trade/market/batchPrice
 * @param {Array<{ symbol: string, outcome: string }>} items
 * @returns {Promise<Record<string, string>>} key 为 `{symbol}:{outcome}`
 */
export function batchMarketPrice(items) {
  const list = (Array.isArray(items) ? items : [])
    .map((row) => ({
      symbol: String(row?.symbol ?? '').trim(),
      outcome: String(row?.outcome ?? '').trim(),
    }))
    .filter((row) => row.symbol && row.outcome);
  if (!list.length) return Promise.reject(new Error('items required'));
  return request.post('/liyuan-trade/market/batchPrice', list);
}

/**
 * 盘口 ticker GET /liyuan-trade/order/ticker/{symbol}
 * @param {string} symbol
 * @param {Object} [params]
 * @param {string} [params.outcome]
 */
export function getOrderTicker(symbol, params = {}) {
  const sym = String(symbol ?? '').trim();
  if (!sym) return Promise.reject(new Error('symbol required'));
  const query = {};
  if (params.outcome) query.outcome = params.outcome;
  return request.get(`/liyuan-trade/order/ticker/${encodeURIComponent(sym)}`, query);
}

/**
 * 查询价格趋势 GET /liyuan-trade/priceTrend
 * @param {Object} params
 * @param {string} params.symbol 交易对，如 BTCUSDT / 市场 id
 * @param {string} [params.timeRange=ALL] ALL | DAY | 1_HOUR | 4_HOUR | MINUTE_1 | MONTH_1 | MONTH_3 | MONTH_6 | WEEK_1 | YEAR_1
 */
export function getPriceTrend(params = {}) {
  const symbol = String(params.symbol ?? '').trim();
  if (!symbol) return Promise.reject(new Error('symbol required'));
  return request.get('/liyuan-trade/priceTrend', {
    symbol,
    timeRange: params.timeRange || 'ALL',
  });
}

/**
 * 创建订单 POST /liyuan-trade/order/order
 * @param {Object} payload
 * @param {string} payload.symbol collection/get 返回的 markets[].id（非 collection id）
 * @param {string} payload.side BUY | SELL
 * @param {number} payload.price 委托价格（美元，如 0.42 表示 42¢）
 * @param {number} payload.quantity 委托数量
 * @param {string} payload.userAddress 用户钱包地址
 * @param {'MAKER'|'TAKER'} payload.tradeRole 订单角色（先调 recommendTradeRole）
 * @param {'LIMIT'|'MARKET'} [payload.type] 订单类型
 * @param {string} [payload.outcome] collection/get 返回的 outcomes 原值（如 Yes / No）
 * @param {string} [payload.source] 调用来源
 */
export function createOrder(payload = {}, options = {}) {
  const symbol = String(payload.symbol ?? '').trim();
  const userAddress = String(payload.userAddress ?? '').trim();
  const tradeRole = String(payload.tradeRole ?? '')
    .trim()
    .toUpperCase();
  if (!symbol) return Promise.reject(new Error('symbol required'));
  if (!userAddress) return Promise.reject(new Error('userAddress required'));
  if (tradeRole !== 'MAKER' && tradeRole !== 'TAKER') {
    return Promise.reject(new Error('tradeRole required'));
  }

  const body = {
    symbol,
    side: String(payload.side ?? '')
      .trim()
      .toUpperCase(),
    price: normalizeOrderPrice(payload.price),
    quantity: normalizeOrderQuantity(payload.quantity),
    userAddress,
    tradeRole,
  };

  if (payload.type) body.type = String(payload.type).trim().toUpperCase();
  if (payload.outcome) body.outcome = String(payload.outcome).trim();
  if (payload.source) body.source = String(payload.source).trim();

  return request.post('/liyuan-trade/order/order', body, {
    timeout: TRADE_ORDER_API_TIMEOUT_MS,
    ...options,
  });
}

/**
 * 获取订单签名信息 GET /liyuan-trade/order/orderSignature/{orderId}
 * @param {number|string} orderId 订单 ID
 * @returns {Promise<{ salt, maker, signer, taker, tokenId, makerAmount, takerAmount, expiration, nonce, feeRateBps, side, signatureType, signature? }>}
 */
export function getOrderSignature(orderId, options = {}) {
  const id = String(orderId ?? '').trim();
  if (!id) return Promise.reject(new Error('orderId required'));
  return request.get(
    `/liyuan-trade/order/orderSignature/${encodeURIComponent(id)}`,
    {},
    {
      timeout: TRADE_ORDER_API_TIMEOUT_MS,
      ...options,
    }
  );
}

/**
 * 提交签名并开启撮合 GET /liyuan-trade/order/taker2OrderBook/{orderId}
 * @param {number|string} orderId 订单 ID
 * @param {string} signature 链上签名
 */
export function submitOrderToOrderBook(orderId, signature, options = {}) {
  const id = String(orderId ?? '').trim();
  const sig = String(signature ?? '').trim();
  if (!id) return Promise.reject(new Error('orderId required'));
  if (!sig) return Promise.reject(new Error('signature required'));
  return request.get(
    `/liyuan-trade/order/taker2OrderBook/${encodeURIComponent(id)}`,
    { signature: sig },
    {
      timeout: TRADE_ORDER_API_TIMEOUT_MS,
      ...options,
    }
  );
}

/**
 * 查询支持的充值代币列表 GET /liyuan-trade/rc/tokens
 * @returns {Promise<Array<{ chainId: string, contractAddress: string, decimals: number, enabled: boolean, name: string, symbol: string, tokenId: string }>>}
 */
export function getRcTokens() {
  return request.get('/liyuan-trade/rc/tokens', undefined, { timeout: 30000 });
}

/**
 * 查询支持的充值链列表 GET /liyuan-trade/rc/chains
 * @returns {Promise<Array<{ chainId: string, enabled: boolean, explorerUrl: string, nativeToken: string, symbol: string }>>}
 */
export function getRcChains() {
  return request.get('/liyuan-trade/rc/chains', undefined, { timeout: 30000 });
}

/**
 * 查询单代币 RC 余额 GET /liyuan-trade/rc/balance/{tokenId}
 * @param {string} tokenId 代币 ID，如 POLYGON_USDC、BSC_USDT
 * @returns {Promise<{ balance: string, tokenId: string }>}
 */
export function getRcTokenBalance(tokenId, options = {}) {
  const id = String(tokenId ?? '').trim();
  if (!id) return Promise.reject(new Error('tokenId required'));
  return request.get(`/liyuan-trade/rc/balance/${encodeURIComponent(id)}`, undefined, {
    timeout: WALLET_DETAIL_TIMEOUT_MS,
    ...options,
  });
}

/**
 * 查询所有代币 RC 余额 GET /liyuan-trade/rc/balances
 * @returns {Promise<Array<{ tokenId: string, symbol: string, balance: string, availableBalance: string, lockedBalance: string, usdValue: string }>>}
 */
export function getRcAllBalances(options = {}) {
  return request.get('/liyuan-trade/rc/balances', undefined, {
    timeout: WALLET_DETAIL_TIMEOUT_MS,
    ...options,
  });
}

/**
 * 获取充值地址 POST /liyuan-trade/rc/address
 * @param {Object} params
 * @param {string} params.chainCode 链代码，如 ETH、BSC_BNB
 * @param {string} params.tokenId 代币 ID，如 ETH_USDT、BSC_USDT
 * @returns {Promise<{ address: string, chainCode: string, memo: string, minDepositAmount: string, tokenId: string }>}
 */
export function getRcDepositAddress(params = {}) {
  const chainCode = String(params.chainCode ?? '').trim();
  const tokenId = String(params.tokenId ?? '').trim();
  if (!chainCode) return Promise.reject(new Error('chainCode required'));
  if (!tokenId) return Promise.reject(new Error('tokenId required'));
  return request.post('/liyuan-trade/rc/address', null, {
    params: { chainCode, tokenId },
  });
}

/**
 * 创建提现订单 POST /liyuan-trade/rc/withdraw/create（提交后进入审核流程）
 * @param {Object} payload
 * @param {string|number} payload.amount 提现金额
 * @param {string} payload.chainCode 链代码
 * @param {string} payload.toAddress 收款地址
 * @param {string} payload.tokenId 代币 ID
 * @returns {Promise<{
 *   id: string,
 *   orderNo: string,
 *   totalAmount: string,
 *   fee: string,
 *   arrivedAmount: string,
 *   chainCode: string,
 *   tokenId: string,
 *   toAddress: string,
 *   status: number,
 *   txHash: string,
 *   createTime: string,
 *   dealTime: string,
 *   memberId: string,
 *   username: string,
 *   walletId: string,
 *   coboRequestId: string,
 *   coboTxId: string,
 *   remark: string
 * }>}
 */
export function createRcWithdrawOrder(payload = {}) {
  const amount = String(payload.amount ?? '').trim();
  const chainCode = String(payload.chainCode ?? '').trim();
  const toAddress = String(payload.toAddress ?? '').trim();
  const tokenId = String(payload.tokenId ?? '').trim();
  if (!amount) return Promise.reject(new Error('amount required'));
  if (!chainCode) return Promise.reject(new Error('chainCode required'));
  if (!toAddress) return Promise.reject(new Error('toAddress required'));
  if (!tokenId) return Promise.reject(new Error('tokenId required'));
  return request.post('/liyuan-trade/rc/withdraw/create', {
    amount,
    chainCode,
    toAddress,
    tokenId,
  });
}

/**
 * 分页查询提现记录 POST /liyuan-trade/rc/withdraws
 * @param {Object} [params]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 * @param {string} [params.beginTime]
 * @param {string} [params.chainCode]
 * @param {string} [params.endTime]
 * @param {number} [params.status]
 * @param {string} [params.tokenId]
 */
export function getRcWithdrawPage(params = {}) {
  const pageNum = params.pageNum ?? 1;
  const pageSize = params.pageSize ?? 10;
  const body = {};
  if (params.beginTime) body.beginTime = params.beginTime;
  if (params.chainCode) body.chainCode = params.chainCode;
  if (params.endTime) body.endTime = params.endTime;
  if (params.status != null && params.status !== '') body.status = params.status;
  if (params.tokenId) body.tokenId = params.tokenId;
  return request.post('/liyuan-trade/rc/withdraws', body, {
    params: { pageNum, pageSize },
  });
}

/**
 * @description 链上身份（users 投影）POST /liyuan-trade/trade-address/add
 * Privy 托管：address + privyWalletId + type:2；自管钱包：address + type:1
 * @param {{ address: string, privyWalletId?: string, type?: number, addressType?: number }} payload
 */
export function addTradeAddress(payload = {}) {
  const address = String(payload.address ?? '').trim();
  if (!address) return Promise.reject(new Error('address required'));
  const privyWalletId = String(payload.privyWalletId ?? '').trim();
  const typeRaw = payload.type ?? payload.addressType;
  let type = typeRaw != null ? Number(typeRaw) : NaN;
  if (type !== 1 && type !== 2) {
    type = privyWalletId ? 2 : 1;
  }
  const body = { address, type };
  if (privyWalletId) body.privyWalletId = privyWalletId;
  return request.post('/liyuan-trade/trade-address/add', body);
}

/**
 * @description 链上身份（users 投影）GET /liyuan-trade/trade-address/list
 * @returns {Promise<Array<{ address: string, addressType: number, privyWalletId: string, userId: string, id: string }>>}
 */
export function listTradeAddresses() {
  return request.get('/liyuan-trade/trade-address/list');
}

/**
 * 积分兑 USDC 比例 GET /liyuan-trade/wallet/convert/points-to-usdc/rate
 * @returns {Promise<number>} 兑换比例（通常为每 1 USDC 所需 POINTS，如 100；或每 1 POINT 可得 USDC，如 0.01）
 */
export function getPointsToUsdcRate(options = {}) {
  return request.get('/liyuan-trade/wallet/convert/points-to-usdc/rate', {}, options);
}

/** 积分兑换 USDC POST /liyuan-trade/wallet/convert/points-to-usdc */
export function convertPointsToUsdc(payload = {}) {
  const pointsAmount = Number(payload.pointsAmount ?? payload.amount);
  if (!Number.isFinite(pointsAmount) || pointsAmount <= 0) {
    return Promise.reject(new Error('pointsAmount required'));
  }
  return request.post('/liyuan-trade/wallet/convert/points-to-usdc', {
    pointsAmount,
  });
}

/**
 * 分页查询积分兑换 USDC 记录 GET /liyuan-trade/wallet/convert/logs
 * @param {Object} [params]
 * @param {number} [params.pageNum=1]
 * @param {number} [params.pageSize=10]
 * @param {string} [params.status] wait_approve | approve | complete | reject（其它态前端按拒绝展示）
 */
export function getPointsConvertLogs(params = {}) {
  return request.get('/liyuan-trade/wallet/convert/logs', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
    ...(params.status ? { status: params.status } : {}),
  });
}

/** 查询或开通用户 Cobo 充值账户 GET /liyuan-evm-pm/pm-chain/cobo/account */
export function coboAccount() {
  return request.get('/liyuan-evm-pm/pm-chain/cobo/account', undefined, { timeout: 30000 });
}

/** 查询跨链充值支持列表 GET /liyuan-evm-pm/pm-chain/cobo/deposit/supported */
export function coboDepositSupported() {
  return request.get('/liyuan-evm-pm/pm-chain/cobo/deposit/supported', undefined, {
    timeout: 30000,
  });
}

/** 查询跨链充值订单列表 GET /liyuan-evm-pm/pm-chain/cobo/deposit/orders */
export function coboDepositOrders(params = {}) {
  return request.get('/liyuan-evm-pm/pm-chain/cobo/deposit/orders', params);
}

/** 查询跨链充值订单列表 GET /liyuan-evm-pm/pm-chain/cobo/deposit/orders */
export function getTadeFee(side, params = {}) {
  return request.get(`/liyuan-trade/order/getTadeFee/${side}`, params);
}
